import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ALL_CODES = [
  { code: 200, label: "OK",                    category: "2xx — Success",   color: "#22c55e" },
  { code: 201, label: "Created",               category: "2xx — Success",   color: "#22c55e" },
  { code: 204, label: "No Content",            category: "2xx — Success",   color: "#22c55e" },
  { code: 301, label: "Moved Permanently",     category: "3xx — Redirect",  color: "#a78bfa" },
  { code: 304, label: "Not Modified",          category: "3xx — Redirect",  color: "#a78bfa" },
  { code: 400, label: "Bad Request",           category: "4xx — Client",    color: "#fb923c" },
  { code: 401, label: "Unauthorized",          category: "4xx — Client",    color: "#fb923c" },
  { code: 403, label: "Forbidden",             category: "4xx — Client",    color: "#fb923c" },
  { code: 404, label: "Not Found",             category: "4xx — Client",    color: "#fb923c" },
  { code: 409, label: "Conflict",              category: "4xx — Client",    color: "#fb923c" },
  { code: 422, label: "Unprocessable Entity",  category: "4xx — Client",    color: "#fb923c" },
  { code: 429, label: "Too Many Requests",     category: "4xx — Client",    color: "#fb923c" },
  { code: 500, label: "Internal Server Error", category: "5xx — Server",    color: "#ef4444" },
  { code: 502, label: "Bad Gateway",           category: "5xx — Server",    color: "#ef4444" },
  { code: 503, label: "Service Unavailable",   category: "5xx — Server",    color: "#ef4444" },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getChoices(correct) {
  const pool = ALL_CODES.filter(c => c.code !== correct.code);
  const picks = shuffle(pool).slice(0, 3);
  return shuffle([correct, ...picks]);
}

const TOTAL = 10;
const TIME_PER_Q = 6;

export default function ApiStatusBlitz({ onDone }) {
  const [queue]               = useState(() => shuffle(ALL_CODES).slice(0, TOTAL));
  const [idx, setIdx]         = useState(0);
  const [choices, setChoices] = useState(() => getChoices(queue[0]));
  const [score, setScore]     = useState(0);
  const [picked, setPicked]   = useState(null);
  const [timeLeft, setTime]   = useState(TIME_PER_Q);

  const question = queue[idx];

  const advance = useCallback(() => {
    if (idx + 1 >= TOTAL) { onDone(score); return; }
    const next = queue[idx + 1];
    setIdx(i => i + 1);
    setChoices(getChoices(next));
    setPicked(null);
    setTime(TIME_PER_Q);
  }, [idx, queue, score, onDone]);

  useEffect(() => {
    if (picked !== null) return;
    if (timeLeft <= 0) { setPicked({ code: null, correct: false }); return; }
    const t = setTimeout(() => setTime(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, picked]);

  useEffect(() => {
    if (picked !== null) {
      const t = setTimeout(advance, 1100);
      return () => clearTimeout(t);
    }
  }, [picked, advance]);

  const choose = (c) => {
    if (picked !== null) return;
    const correct = c.code === question.code;
    if (correct) setScore(s => s + 1);
    setPicked({ code: c.code, correct });
  };

  const timerPct = (timeLeft / TIME_PER_Q) * 100;
  const timerColor = timeLeft <= 2 ? "#ef4444" : timeLeft <= 3 ? "#fb923c" : "#22c55e";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>Q {idx + 1}/{TOTAL} · Score: {score}</span>
        <span style={{ fontFamily: "monospace", fontWeight: 700, color: timerColor, fontSize: "1rem" }}>{timeLeft}s</span>
      </div>

      <div style={{ height: 3, borderRadius: 2, background: "var(--border)", marginBottom: 20, overflow: "hidden" }}>
        <motion.div
          style={{ height: "100%", borderRadius: 2, background: timerColor }}
          animate={{ width: `${timerPct}%` }}
          transition={{ duration: 0.9, ease: "linear" }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 6 }}>What does this status code mean?</p>
            <p style={{
              fontSize: "3rem", fontWeight: 800, letterSpacing: "-0.04em",
              color: question.color, lineHeight: 1,
            }}>{question.code}</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {choices.map((c) => {
              const isCorrect = picked && c.code === question.code;
              const isWrong   = picked && picked.code === c.code && !picked.correct;
              let borderColor = "var(--border)";
              let bg = "var(--bg-card)";
              if (isCorrect) { borderColor = "rgba(34,197,94,0.5)"; bg = "rgba(34,197,94,0.08)"; }
              if (isWrong)   { borderColor = "rgba(239,68,68,0.5)";  bg = "rgba(239,68,68,0.08)"; }

              return (
                <div key={c.code} onClick={() => choose(c)} style={{
                  padding: "12px 14px", borderRadius: 10, fontSize: "0.8rem",
                  border: `1px solid ${borderColor}`, background: bg,
                  cursor: picked ? "default" : "pointer", color: "var(--text)",
                  transition: "border-color 100ms ease, background 100ms ease",
                  lineHeight: 1.3,
                }}
                  onMouseEnter={e => { if (!picked) { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--bg-subtle)"; } }}
                  onMouseLeave={e => { if (!picked) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg-card)"; } }}
                >
                  <span style={{ display: "block", fontWeight: 600, marginBottom: 2 }}>{c.label}</span>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.72rem" }}>{c.category}</span>
                </div>
              );
            })}
          </div>

          {picked && picked.code === null && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 12, textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              Time's up — it was <strong style={{ color: "var(--text)" }}>{question.label}</strong>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
